import * as React from "react";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { styles } from "./CalendarDay.styles";

// util
import {
  IEventColoringSettings,
  IEventCategoryColoring,
} from "@clockwise/client-commons/src/util/event-category-coloring";
import { getNormalPositioners } from "@clockwise/client-commons/src/util/event-position";
import { getTimeAllocation } from "@clockwise/client-commons/src/util/event";
import {
  convertTimeMinSlotToHourMinutes,
  convertTimeMaxSlotToHourMinutes,
} from "@clockwise/client-commons/src/util/time-slot";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";

// components
import { RelativeView, DataMapper, DisplayData } from "@components/relative-view";
import {
  CalendarDayEventDisplayData,
  calendarDayEventDataMapperFactory,
  CalendarDayIEvent,
  CalendarCurrentTimeMarker,
  CalendarDayEventMode,
} from "@components/calendar";
import { CalendarDayEventTooltipOptions } from "../calendar-day-event";

// renderers
import {
  renderWorkingHoursFactory,
  renderFragmentedTimeFactory,
  WorkingHoursRendererState,
  IWorkingHoursDaySetting,
  FragmentedTimeRendererState,
} from "../renderers";

export interface CalendarDayAugmentedEvent {
  tooltipOptions?: CalendarDayEventTooltipOptions;
  event: CalendarDayIEvent;
}

export interface CalendarDayProps extends WithStyles<typeof styles> {
  day: ZonedMoment;
  onClick: (calendarId: string, externalEventId: string) => void;
  userId: string;
  calendarIds: string[];
  colorSettings?: IEventColoringSettings<IEventCategoryColoring>;
  events: CalendarDayAugmentedEvent[];
  daySetting?: IWorkingHoursDaySetting;
  showFragmentedTime?: boolean;
  showWorkingHours?: boolean;
  mode?: CalendarDayEventMode;
}

export interface CalendarDayState extends WorkingHoursRendererState, FragmentedTimeRendererState {
  dataMappers: Record<string, DataMapper<CalendarDayEventDisplayData>>;
}

class CalendarDayComponent extends React.Component<CalendarDayProps, CalendarDayState> {
  constructor(props: CalendarDayProps) {
    super(props);

    const dataMappers = {
      event: calendarDayEventDataMapperFactory({ ...this.props }),
      daySetting: renderWorkingHoursFactory(true).bind(this),
      fragmentedTime: renderFragmentedTimeFactory().bind(this),
    };

    this.state = {
      dataMappers,
      minStart: 0,
      maxEnd: 48,
    };
  }

  private getDisplayData = () => {
    const {
      events,
      day,
      onClick,
      userId,
      calendarIds,
      colorSettings,
      daySetting,
      showFragmentedTime,
      showWorkingHours,
    } = this.props;

    const tooltipMap: Record<string, CalendarDayEventTooltipOptions> = {};

    const nonAugmentedEvents = events.map((event) => {
      // take advantage of this map to generate tooltipMap simultaneously
      if (event.tooltipOptions) {
        tooltipMap[event.event.eventKey.externalEventId] = event.tooltipOptions;
      }

      return event.event;
    });

    const eventPositioners = getNormalPositioners(nonAugmentedEvents, [day]);

    const displayData: DisplayData<any>[] = [
      ...eventPositioners.map((eventPositioner) => {
        return {
          type: "event",
          payload: {
            eventPositioner,
            onClick,
            userId,
            calendarIds,
            colorSettings,
            tooltipOptions: tooltipMap[eventPositioner.event.eventKey.externalEventId],
          } as CalendarDayEventDisplayData,
        };
      }),
    ];

    if (daySetting && showWorkingHours) {
      displayData.push({
        type: "daySetting",
        payload: daySetting,
      });
    }

    if (showFragmentedTime && daySetting) {
      const dayStartMillis = day.valueOf();
      const startHourMinutes = convertTimeMinSlotToHourMinutes(daySetting.setting.minSlot);
      const endHourMinutes = convertTimeMaxSlotToHourMinutes(daySetting.setting.maxSlot);
      const startMillis = dayStartMillis + startHourMinutes.minutesIntoDay * 60000;
      const endMillis = dayStartMillis + endHourMinutes.minutesIntoDay * 60000;

      const { fragmentedRanges } = getTimeAllocation(nonAugmentedEvents, startMillis, endMillis);

      fragmentedRanges.forEach((fragmentedRange) =>
        displayData.push({
          type: "fragmentedTime",
          payload: {
            ...fragmentedRange,
          },
        }),
      );
    }

    return displayData;
  };

  public render() {
    return (
      <RelativeView displayData={this.getDisplayData()} dataMappers={this.state.dataMappers}>
        <CalendarCurrentTimeMarker day={this.props.day} />
      </RelativeView>
    );
  }
}

export const CalendarDay = withStyles(styles)(CalendarDayComponent);
